import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { FlowType } from "@redotech/redo-model/return-flow";
import {
  CLAIM_REASON_TITLE,
  reasonTitleForFlowType,
  RETURN_REASON_TITLE,
  WARRANTY_REASON_TITLE,
} from "../util";

export const REASON_CONDITION_NAMES = [
  RETURN_REASON_TITLE,
  CLAIM_REASON_TITLE,
  WARRANTY_REASON_TITLE,
] as const;

export type ReasonConditionName = (typeof REASON_CONDITION_NAMES)[number];

export const REASON_CONDITION_OPTIONS_BY_NAME = {
  [RETURN_REASON_TITLE]: {
    customTitle: undefined,
    optionKey: "returnReasonOptions",
    returnTypes: [ReturnTypeEnum.RETURN],
  },
  [CLAIM_REASON_TITLE]: {
    customTitle: CLAIM_REASON_TITLE,
    optionKey: "claimReasonOptions",
    returnTypes: [ReturnTypeEnum.CLAIM],
  },
  [WARRANTY_REASON_TITLE]: {
    customTitle: WARRANTY_REASON_TITLE,
    optionKey: "warrantyReasonOptions",
    returnTypes: [ReturnTypeEnum.WARRANTY],
  },
} as const satisfies Record<
  ReasonConditionName,
  {
    customTitle: ReasonConditionName | undefined;
    optionKey: ReasonOptionKey;
    returnTypes: readonly ReturnTypeEnum[];
  }
>;

type ReasonOptionKey =
  | "claimReasonOptions"
  | "returnReasonOptions"
  | "warrantyReasonOptions";

interface ReasonOptionsInput {
  claimReasonOptions: string[];
  conditionName: string;
  returnReasonOptions: string[];
  warrantyReasonOptions: string[];
}

export function isReasonConditionName(
  name: string | undefined,
): name is ReasonConditionName {
  return REASON_CONDITION_NAMES.includes(name as ReasonConditionName);
}

export function getReasonConditionCustomTitle(name: string) {
  return isReasonConditionName(name)
    ? REASON_CONDITION_OPTIONS_BY_NAME[name].customTitle
    : undefined;
}

export function getReasonConditionReturnTypes(name: string) {
  return isReasonConditionName(name)
    ? REASON_CONDITION_OPTIONS_BY_NAME[name].returnTypes
    : undefined;
}

export function getReasonConditionNameForReturnTypes(
  returnTypes: readonly ReturnTypeEnum[] | undefined,
) {
  if (returnTypes?.length !== 1) {
    return undefined;
  }

  const returnType = returnTypes[0];
  if (!returnType) {
    return undefined;
  }

  return REASON_CONDITION_NAMES.find((name) =>
    (
      REASON_CONDITION_OPTIONS_BY_NAME[name]
        .returnTypes as readonly ReturnTypeEnum[]
    ).includes(returnType),
  );
}

export function getReturnReasonConditionName({
  customTitle,
  flowType,
  returnTypes,
}: {
  customTitle?: string;
  flowType: FlowType;
  returnTypes?: readonly ReturnTypeEnum[];
}) {
  const conditionNameFromReturnTypes =
    getReasonConditionNameForReturnTypes(returnTypes);
  if (flowType === FlowType.RETURN_LOCATION && conditionNameFromReturnTypes) {
    return conditionNameFromReturnTypes;
  }

  if (
    flowType === FlowType.RETURN_LOCATION &&
    isReasonConditionName(customTitle)
  ) {
    return customTitle;
  }

  return reasonTitleForFlowType(flowType);
}

export function getReasonOptionsForReturnReasonCondition({
  claimReasonOptions,
  conditionName,
  returnReasonOptions,
  warrantyReasonOptions,
}: ReasonOptionsInput) {
  const optionKey = isReasonConditionName(conditionName)
    ? REASON_CONDITION_OPTIONS_BY_NAME[conditionName].optionKey
    : "returnReasonOptions";

  return { claimReasonOptions, returnReasonOptions, warrantyReasonOptions }[
    optionKey
  ];
}
